export const PROTOCOL_VERSION = 1
export const PING_EVERY_MS = 5_000
export const SILENCE_LIMIT_MS = 15_000
export const MAX_PIN_FAILURES = 3
export const MAX_FRAME_BYTES = 64 * 1024

// Clear frames: the handshake, before a session key exists.
export type ClearFrame =
    | { t: 'pake1'; v: number; pA: string; n: string }
    | { t: 'pake2'; pB: string; n: string; cB: string }
    | { t: 'pake3'; cA: string }
    | { t: 'resume'; v: number; sid: string; n: string }
    | { t: 'resume_ok'; n: string }
    | { t: 'busy' }
    | { t: 'refused'; reason: RefusedReason }

export type RefusedReason = 'version' | 'bad_pin' | 'locked' | 'unknown_session' | 'not_pairing'

export type ScreenContext = 'sell' | 'returns' | 'stock_count' | 'customer_search' | 'other'
export type ByeReason = 'shift_closed' | 'unpaired' | 'signed_out' | 'denied'

export interface CartLine {
    name: string
    qty: number
    unit_price: number
    discount: number
    total: number
}

export type CompanionMessage =
    | { type: 'hello'; v: number; device_name: string; platform: 'android' | 'web' }
    | { type: 'scan'; id: string; code: string; format?: string }
    | { type: 'want'; view: 'scan' | 'cart' }
    | { type: 'ping' }

export type MasterMessage =
    | { type: 'welcome'; v: number; terminal_no: number; terminal_name: string; branch: string; staff: string }
    | { type: 'context'; screen: ScreenContext }
    | { type: 'cart'; lines: CartLine[]; subtotal: number; discount: number; vat: number; total: number; customer?: string }
    | { type: 'result'; id: string; ok: boolean; title?: string; detail?: string; total?: number }
    | { type: 'bye'; reason: ByeReason }
    | { type: 'pong' }

type Rule = 'string' | 'number' | 'boolean' | 'hex' | readonly string[] | ((v: unknown) => boolean)
type Shape = Record<string, Rule | { optional: Rule }>

const isHex = (v: unknown) => typeof v === 'string' && v.length > 0 && v.length % 2 === 0 && /^[0-9a-f]+$/.test(v)
const isInt = (v: unknown) => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0
const isLine = (v: unknown) => check(v, { name: 'string', qty: 'number', unit_price: 'number', discount: 'number', total: 'number' })

const ok = (rule: Rule, v: unknown): boolean => {
    if (typeof rule === 'function') return rule(v)
    if (Array.isArray(rule)) return typeof v === 'string' && rule.includes(v)
    if (rule === 'hex') return isHex(v)
    if (rule === 'number') return typeof v === 'number' && Number.isFinite(v)
    if (rule === 'string') return typeof v === 'string' && v.length <= 512
    return typeof v === rule
}

function check(v: unknown, shape: Shape): boolean {
    if (typeof v !== 'object' || v === null || Array.isArray(v)) return false
    const o = v as Record<string, unknown>
    for (const [k, rule] of Object.entries(shape)) {
        if (typeof rule === 'object' && !Array.isArray(rule) && 'optional' in rule) {
            if (o[k] !== undefined && !ok(rule.optional, o[k])) return false
        } else if (!ok(rule as Rule, o[k])) return false
    }
    return true
}

const CLEAR: Record<ClearFrame['t'], Shape> = {
    pake1: { v: isInt, pA: 'hex', n: 'hex' },
    pake2: { pB: 'hex', n: 'hex', cB: 'hex' },
    pake3: { cA: 'hex' },
    resume: { v: isInt, sid: 'hex', n: 'hex' },
    resume_ok: { n: 'hex' },
    busy: {},
    refused: { reason: ['version', 'bad_pin', 'locked', 'unknown_session', 'not_pairing'] },
}

const FROM_COMPANION: Record<CompanionMessage['type'], Shape> = {
    hello: { v: isInt, device_name: 'string', platform: ['android', 'web'] },
    scan: { id: 'string', code: 'string', format: { optional: 'string' } },
    want: { view: ['scan', 'cart'] },
    ping: {},
}

const FROM_MASTER: Record<MasterMessage['type'], Shape> = {
    welcome: { v: isInt, terminal_no: isInt, terminal_name: 'string', branch: 'string', staff: 'string' },
    context: { screen: ['sell', 'returns', 'stock_count', 'customer_search', 'other'] },
    cart: {
        lines: (v) => Array.isArray(v) && v.length <= 500 && v.every(isLine),
        subtotal: 'number',
        discount: 'number',
        vat: 'number',
        total: 'number',
        customer: { optional: 'string' },
    },
    result: { id: 'string', ok: 'boolean', title: { optional: 'string' }, detail: { optional: 'string' }, total: { optional: 'number' } },
    bye: { reason: ['shift_closed', 'unpaired', 'signed_out', 'denied'] },
    pong: {},
}

const parseBy = <T>(key: 't' | 'type', table: Record<string, Shape>, v: unknown): T | null => {
    if (typeof v !== 'object' || v === null) return null
    const tag = (v as Record<string, unknown>)[key]
    if (typeof tag !== 'string' || !Object.prototype.hasOwnProperty.call(table, tag)) return null
    return check(v, table[tag]) ? (v as T) : null
}

export const parseClearFrame = (v: unknown) => parseBy<ClearFrame>('t', CLEAR, v)
export const parseCompanionMessage = (v: unknown) => parseBy<CompanionMessage>('type', FROM_COMPANION, v)
export const parseMasterMessage = (v: unknown) => parseBy<MasterMessage>('type', FROM_MASTER, v)

export const isBoxFrame = (v: unknown): v is { t: 'box'; s: number; d: string } =>
    check(v, { t: ['box'], s: isInt, d: (x) => typeof x === 'string' && x.length <= MAX_FRAME_BYTES && /^[A-Za-z0-9+/]*={0,2}$/.test(x) })
